// PO Spec Phase 4 §3 (2026-06-21) — "Resources Accessed" recorder hook.
//
// Single source for firing the append-only resource-access event from the two
// Employee surfaces that surface a bereavement resource:
//   - the Companion red-flag handoff (crisis-link clicks), and
//   - the portal entitlements panel (RAG-grounded policy-chunk reads).
//
// Fire-and-forget by design: the server is best-effort (a write failure is
// logged server-side, never surfaced) so analytics can never block a grieving
// user from reaching a crisis line or reading their entitlements. When there
// is no active `caseId` (e.g. the portal rendered with no open case) the
// recorder is a no-op — there is nothing to scope the event to.
//
// Privacy: only the `resourceKind` family (and an optional opaque ref) crosses
// the wire — never a URL or user-facing copy. See the
// `RecordResourceAccessRequestSchema` comment in `@besideu/shared/cases`.

import { useMutation } from '@tanstack/react-query';
import type { ResourceAccessKind } from '@besideu/shared/cases';
import { api } from './api.ts';

export function useRecordResourceAccess(caseId: string | undefined) {
  const mutation = useMutation({
    mutationFn: (input: { resourceKind: ResourceAccessKind; resourceRef?: string }) =>
      api.post<void>(`/api/employee/resources/record/${caseId}`, input),
  });
  return (resourceKind: ResourceAccessKind, resourceRef?: string) => {
    if (!caseId) return;
    // Omit `resourceRef` entirely when absent rather than passing `undefined`
    // (exactOptionalPropertyTypes); the server treats a missing ref as null.
    mutation.mutate(resourceRef === undefined ? { resourceKind } : { resourceKind, resourceRef });
  };
}
